import type { ReactNode } from 'react';
import type { LinkProps } from '@tanstack/react-router';
import CountUp from 'react-countup';
import { ArrowRightIcon } from 'lucide-react';
import { Skeleton } from '@ui/skeleton.tsx';
import { LinkProtected } from '@r/components/link-protected/link-protected.tsx';
import { cn } from '@r/utils/utils.ts';

interface DashboardStatsSectionProps {
  title: string;
  children: ReactNode;
  className?: string;
  link?: { to: LinkProps['to']; label: string };
}

export function DashboardStatsSection({
  title,
  children,
  className,
  link
}: DashboardStatsSectionProps) {
  return (
    <section className="flex flex-col p-4 space-y-3 rounded-3xl shadow-sm border border-foreground/10">
      <div className="flex items-center justify-between min-h-8">
        <h3 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">
          {title}
        </h3>
        {link && (
          <LinkProtected
            to={link.to}
            className="inline-flex items-center text-sm font-medium rounded-sm text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {link.label}
            <ArrowRightIcon size={14} className="ml-1.5" />
          </LinkProtected>
        )}
      </div>
      <div className={cn('grid grid-cols-1 gap-3 sm:grid-cols-2', className)}>{children}</div>
    </section>
  );
}

interface DashboardStatTileProps {
  title: string;
  icon: ReactNode;
  value: number;
  valueSuffix?: string;
  decimals?: 0 | 2;
  className?: string;
}

export function DashboardStatTile({
  title,
  icon,
  value,
  valueSuffix,
  decimals = 0,
  className
}: DashboardStatTileProps) {
  return (
    <div
      className={cn(
        'flex flex-col justify-between p-4 space-y-3 rounded-2xl shadow-sm bg-card',
        className
      )}
    >
      <div className="flex items-start space-x-2 text-sm font-medium text-muted-foreground">
        <span className="mt-0.5">{icon}</span>
        <span>{title}</span>
      </div>
      <CountUp
        className="text-2xl font-bold"
        end={value}
        duration={2}
        suffix={valueSuffix}
        separator={' '}
        decimal={','}
        decimals={decimals}
      />
    </div>
  );
}

export function DashboardStatTileSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn('flex flex-col p-4 space-y-3 rounded-2xl shadow-sm bg-card', className)}>
      <Skeleton className="w-8/12 h-5" />
      <Skeleton className="w-5/12 h-8" />
    </div>
  );
}
